import type {As, ComponentWithAs} from '@chakra-ui/react';
import {forwardRef} from '@chakra-ui/react';
import type {ThemingProps} from '@chakra-ui/styled-system';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import {dataAttr} from '@utils';
import type {PropsWithChildren} from 'react';

import {useSlots} from '../../hooks/useSlots';

import {DefaultWidgetError, WidgetError} from './Error';
import {WidgetSkeleton} from './Skeleton';
import {useStyles} from './context';
import {parts} from './theme';
import {WidgetBody, WidgetEmpty, WidgetFooter, WidgetHeader} from '.';

type Props = PropsWithChildren<WidgetContext> & Pick<ThemingProps, 'size'>;

export type WidgetContext = {isInteractive?: boolean; isEmpty?: boolean; isLoading?: boolean; hasError?: boolean};

type SlotsConfig = {
  header: typeof WidgetHeader;
  body: typeof WidgetBody;
  footer: typeof WidgetFooter;
  empty: typeof WidgetEmpty;
  error: typeof WidgetError;
  skeleton: typeof WidgetSkeleton;
};

export const WidgetContainer: ComponentWithAs<As, Props> = forwardRef<Props, As>(
  ({isInteractive, isEmpty, isLoading, hasError, children, ...props}, ref) => {
    const {container} = useStyles();
    const [slots, childrenWithoutSlots] = useSlots<SlotsConfig, WidgetContext>(
      children,
      {
        header: WidgetHeader,
        body: WidgetBody,
        footer: WidgetFooter,
        empty: WidgetEmpty,
        error: WidgetError,
        skeleton: WidgetSkeleton,
      },
      parts,
      {isInteractive, isEmpty, isLoading},
    );

    return (
      <ChakraDiv
        __css={container}
        {...props}
        ref={ref}
        {...parts.container.attrs}
        data-interactive={dataAttr(isInteractive)}>
        {slots.header}
        {/* // Render the widget body */}
        {(() => {
          if (isLoading) {
            return slots.skeleton ?? <WidgetSkeleton />;
          }
          if (hasError) {
            return slots.error ?? <DefaultWidgetError />;
          }
          if (isEmpty) {
            return slots.empty;
          }

          return slots.body;
        })()}
        {childrenWithoutSlots}
        {slots.footer}
      </ChakraDiv>
    );
  },
);

export type {Props as WidgetProps};
